import { __assign, __spreadArray } from "tslib";
import { appUrlResource, fullPermissionApps, properWildcardDomainRegex } from '../constants';
import { FileOpenPreference, } from '../hosted-web-content-sdk.interface';
import { LogLevel } from '../interfaces';
import { HubNames } from '../messages.interface';
import { CaseInsensitiveSet } from './CaseInsensitiveSet';
import { getCurrentFeatureFlagsState } from './featureFlags';
export var getValidDomainFromUrl = function (url) { return url && new URL(url).host; };
/**
 * to validate origin against valid domains for a hosted web content
 */
export var isValidUrlForApp = function (url, logger, appDefinition, appContext) {
    try {
        var hydratedUrl = new URL(hydrateUrl(url, appContext));
        return (appDefinition !== undefined &&
            isUrlInDomainList(hydratedUrl, getValidDomainsForApp(appDefinition, appContext), logger));
    }
    catch (_a) {
        logger.log(LogLevel.Error, 'isValidUrlForApp', 'Invalid url', null);
        return false;
    }
};
export var getInvalidDomains = function (domains) {
    var invalidDomains = [];
    for (var _i = 0, domains_1 = domains; _i < domains_1.length; _i++) {
        var domain = domains_1[_i];
        if (!domain.match(properWildcardDomainRegex)) {
            invalidDomains.push(domain);
        }
    }
    return invalidDomains;
};
export var getValidDomainsForApp = function (appDefinition, appContext) {
    var _a, _b;
    var validDomains = new CaseInsensitiveSet();
    if (appDefinition.validDomains) {
        appDefinition.validDomains.forEach(validDomains.add);
    }
    (_a = appDefinition.pages.configurable) === null || _a === void 0 ? void 0 : _a.forEach(function (ct) {
        return validDomains.add(getValidDomainFromUrl(hydrateUrl(ct.configurationUrl, appContext)));
    });
    (_b = appDefinition.pages.static) === null || _b === void 0 ? void 0 : _b.forEach(function (st) {
        if (st.contentUrl) {
            validDomains.add(getValidDomainFromUrl(hydrateUrl(st.contentUrl, appContext)));
        }
    });
    return Array.from(validDomains.values());
};
// TODO: Update all full trust apps in MT and remove list of overrides in TMP and TSW
// https://domoreexp.visualstudio.com/MSTeams/_workitems/edit/1049320
export var hasFullPermission = function (appId) { return appId && fullPermissionApps.indexOf(appId.toLowerCase()) !== -1; };
export var hasFullPermissionOrIsLobApp = function (appId, tenantId) { return hasFullPermission(appId) || !!tenantId; };
/**
 * It returns the resource which can be used to fetch the AuthToken by the app.
 * For Full trust app in Teams, we honor the resources they send in getAuthToken API if its non-empty
 * And for any other case we send back resource defined in appDefinition when its called in the same hosted domain
 */
export var getValidatedResources = function (origin, resources, hubInfo, appDefinition, appContext) {
    var _a;
    if ((hubInfo.name === HubNames.teams || hubInfo.name === HubNames.teamsModern) &&
        resources &&
        resources.length > 0 &&
        (!appDefinition /* TODO make app definition required for shareTo Teams, Digital Signage */ ||
            appDefinition.isFullTrust ||
            hasFullPermission(appDefinition.id))) {
        return Promise.resolve(resources);
    }
    else if ((_a = appDefinition === null || appDefinition === void 0 ? void 0 : appDefinition.webApplicationInfo) === null || _a === void 0 ? void 0 : _a.resource) {
        var resourceForApp = getResourceForApp(appDefinition, appContext);
        if (resourceForApp && isValidResource(resourceForApp, origin)) {
            return Promise.resolve([resourceForApp]);
        }
        else {
            return Promise.reject('App resource defined in manifest and iframe origin do not match');
        }
    }
    else {
        return Promise.reject('App webApplicationInfo or resource not defined in manifest');
    }
};
/**
 * Check for variable placeholder like {teamSiteDomain} and fills in values from app context
 * https://{teamSiteDomain}/test changes to  https://azure.websites.com/test
 * Limits the placeholding values to the fields allowed in domain of the Url
 * @param url input url
 * @param appContext web Context of the tab
 */
export var hydrateUrl = function (url, appContext) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (!appContext) {
        return url;
    }
    // construct an object with just the properties we want to replace before calling into formatUrl
    var propsToReplace = {
        // v1 structure
        teamSiteDomain: ((_a = appContext.sharePointSite) === null || _a === void 0 ? void 0 : _a.teamSiteDomain) || '',
        teamSitePath: ((_b = appContext.sharePointSite) === null || _b === void 0 ? void 0 : _b.teamSitePath) || '',
        teamSiteUrl: ((_c = appContext.sharePointSite) === null || _c === void 0 ? void 0 : _c.teamSiteUrl) || '',
        mySiteDomain: ((_d = appContext.sharePointSite) === null || _d === void 0 ? void 0 : _d.mySiteDomain) || '',
        mySitePath: ((_e = appContext.sharePointSite) === null || _e === void 0 ? void 0 : _e.mySitePath) || '',
        channelRelativeUrl: ((_f = appContext.channel) === null || _f === void 0 ? void 0 : _f.relativeUrl) || '',
        // v2 structure
        sharePointSite: {
            teamSiteDomain: ((_g = appContext.sharePointSite) === null || _g === void 0 ? void 0 : _g.teamSiteDomain) || '',
            teamSitePath: ((_h = appContext.sharePointSite) === null || _h === void 0 ? void 0 : _h.teamSitePath) || '',
            teamSiteUrl: ((_j = appContext.sharePointSite) === null || _j === void 0 ? void 0 : _j.teamSiteUrl) || '',
            mySiteDomain: ((_k = appContext.sharePointSite) === null || _k === void 0 ? void 0 : _k.mySiteDomain) || '',
            mySitePath: ((_l = appContext.sharePointSite) === null || _l === void 0 ? void 0 : _l.mySitePath) || '',
        },
        channel: {
            relativeUrl: ((_m = appContext.channel) === null || _m === void 0 ? void 0 : _m.relativeUrl) || '',
        },
    };
    return formatUrl(url, propsToReplace);
};
export var getResourceForApp = function (appDefinition, appContext) { var _a, _b; return ((_a = appDefinition.webApplicationInfo) === null || _a === void 0 ? void 0 : _a.resource) && hydrateUrl((_b = appDefinition.webApplicationInfo) === null || _b === void 0 ? void 0 : _b.resource, appContext); };
/**
 * Valid resources will contain the domain in the app url https:// or in the format of api://{domain}/
 * @param resource The resource being requested by the app
 * @param origin The calling app url
 */
export var isValidResource = function (resource, origin) {
    if (resource && origin) {
        if (resource === origin) {
            return true;
        }
        else {
            var domain = getValidDomainFromUrl(origin);
            var resourceTestExpression = appUrlResource.validResourcePattern.replace(appUrlResource.resourceDomainParameter, domain);
            return new RegExp(resourceTestExpression, 'i').test(resource);
        }
    }
    else {
        return false;
    }
};
/**
 * Valid resource for prefetch will be a well formed URL using https or api protocol
 *
 * @param resource resource string to validate
 * @returns true if the provided resource string is a valid resourceUrl, false otherwise
 */
export var isValidResourceForPrefetch = function (resource) {
    try {
        var url = new URL(resource);
        return url.protocol === 'https:' || url.protocol === 'api:';
    }
    catch (_a) {
        return false;
    }
};
/**
 * Validate whether the given URL is a part of the given list of domains
 * @param {string} url - URL to be validated
 * @param {string[]} domains - List of valid domains
 * @returns {boolean} - True if the url belongs to the valid domains, else False
 */
export var isUrlInDomainList = function (url, domains, logger) {
    // Normalize the tab URL removing the default port if present
    var normalizedUrl = removeDefaultPort(url);
    // Keep track of all the domains already checked
    var checkedDomains = new CaseInsensitiveSet();
    var domainsToCheckAgainst = __spreadArray([], domains, true);
    var invalidDomains = getInvalidDomains(__spreadArray([], domains, true));
    if (invalidDomains.length > 0) {
        // TODO Bug #7306962: When logging is implemented, log invalid domains to hub
        logger.log(LogLevel.Debug, 'isUrlInDomainList', "Invalid domain detected with ".concat(invalidDomains, "}"));
        // Until we have logging, flag = false by default
        if (getCurrentFeatureFlagsState().wildcardValidDomainRuleDetection) {
            domainsToCheckAgainst = domainsToCheckAgainst.filter(function (domain) { return !invalidDomains.includes(domain); });
        }
    }
    // Check the URL against each domain in the list
    for (var _i = 0, domainsToCheckAgainst_1 = domainsToCheckAgainst; _i < domainsToCheckAgainst_1.length; _i++) {
        var domain = domainsToCheckAgainst_1[_i];
        if (!checkedDomains.add(domain)) {
            continue;
        }
        var formattedDomain = transformDomainFromManifestFormatToRegexFormat(domain);
        // Check both the original URL and the normalized one against the pattern since
        // the developer might have specified the default port in their valid domains
        var regExPattern = new RegExp("^https://".concat(formattedDomain, "((/|\\?).*)?$"), 'i');
        if (normalizedUrl.match(regExPattern)) {
            return true;
        }
    }
    return false;
};
/**
 * Takes given domain to convert periods to literal periods in regex string and replace any asterisks with a regex string representing a wildcard.
 * Throws error if feature flag wildcardValidDomainRuleDetection is enabled and the given domain is improper. Proper format is where:
 * 1. Wildcard (‘*’) is the only character in a segment if a wildcard is present.
 * 2. If using a wildcard, there may be no segments preceding it that do not contain wildcards.
 * e.g., “*.*.domain.com” is valid but 'foo.*.myteam.domain.com' is not valid
 * Please check out our official docs here: https://learn.microsoft.com/en-us/microsoftteams/platform/resources/schema/manifest-schema?view=msteams-client-js-latest#validdomains
 * @param domain The domain string to format
 * @returns the transformed domain with any periods and asterisks converted to JS regex syntax string
 */
export var transformDomainFromManifestFormatToRegexFormat = function (domain) {
    if (getCurrentFeatureFlagsState().wildcardValidDomainRuleDetection === true &&
        getInvalidDomains([domain]).length > 0) {
        throw new Error('The wildcardValidDomainRuleDetection flag is enabled and an improper wildcard domain segment was detected: ' +
            domain);
    }
    var domainParts = domain.split('.');
    var formattedDomain = '';
    for (var j = 0; j < domainParts.length; j++) {
        // TODO: This work can easily be done using replace(). We are using a temporary workaround to avoid using replace() as part
        // of security bug #6518115. Once featureflag wildcardValidDomainRuleDetection is phased out, we should look into using replaceAll()
        // or some way to get rid of ALL wildcards within a subdomain rather than just the first occurrence of one.
        var segment = domainParts[j];
        var wildcardPosition = segment.indexOf('*');
        formattedDomain += j > 0 ? '[.]' : '';
        if (wildcardPosition > -1) {
            var beforeWildcard = segment.slice(0, wildcardPosition);
            var afterWildcard = segment.slice(wildcardPosition + 1);
            var segmentWithRemovedWildcard = beforeWildcard + '[^/^.]+' + afterWildcard;
            formattedDomain += segmentWithRemovedWildcard;
        }
        else {
            formattedDomain += segment;
        }
    }
    return formattedDomain;
};
export var removeDefaultPort = function (url) {
    return url.href;
};
/**
 * Formats a URL using full AppContext information (both v2 version and v1 ContextBridge representation).
 * Will replace all possible placeholders for both of the context versions, including for properties not present on provided context value.
 * For these properties that don't have value, empty string will be used instead.
 *
 * @param url Initial URL to perform replacement on
 * @param context AppContext information in v2 format
 * @param transformedContext Transformed context information in v1 format
 * @returns URL with all the placeholder values replaced with information from either of the context objects, or empty string if not value present.
 */
export var formatUrlWithFullContext = function (url, context, transformedContext) {
    // format with v2 context first
    var formattedUrl = formatUrl(url, getContextWithDefaults(context));
    // run another replacement against v1 format
    formattedUrl = formatUrl(formattedUrl, transformedContext);
    return formattedUrl;
};
/**
 * Formats an URL in order to make it ready for navigation. The process
 * involves replacing all of the placeholders in the URL with their values in the provided object
 * @param url url to format
 * @param propertyValue value of property to use for placeholder replacement
 * @param propertyPath path for current replacement value
 * @returns URL with placeholded values of property supplied
 */
export var formatUrl = function (url, propertyValue, propertyPath) {
    if (propertyPath === void 0) { propertyPath = []; }
    var qspStartIndex = url.indexOf('?');
    var urlPart = '';
    var qspPart = '';
    if (qspStartIndex > 0) {
        // url substring containing path i.e. before query search params
        urlPart = url.substring(0, qspStartIndex);
        // url substring containing query search params
        qspPart = url.substring(qspStartIndex);
    }
    else {
        // if no qsp present in URL
        urlPart = url;
    }
    if (urlPart) {
        urlPart = formatUrlSubpartWithEncoding(urlPart, propertyValue, propertyPath, function (value) {
            return encodeURI(value);
        });
    }
    if (qspPart) {
        qspPart = formatUrlSubpartWithEncoding(qspPart, propertyValue, propertyPath, function (value) {
            return encodeURIComponent(value);
        });
    }
    return urlPart + qspPart;
};
/**
 * Formats a URL part in order to make it ready for navigation. The process
 * involves replacing all of the placeholders in the URL with their values in the provided object. It is case-sensitive
 * @param urlPart url or its subpart which needs to be formatted with a relevant @param encoder
 * @param propertyValue value of property to use for placeholder replacement
 * @param propertyPath path for current replacement value
 * @param encoder An encoder to format/encode values before being placeholded
 * @returns placeholded urlPart
 *
 * For eg.
 * For property object:
 * Input(propertyValue): {
 *                        "app":{
 *                              "host":"Orange",
 *                              "name" "Example"
 *                             }
 *                       }
 * Input(urlPart): "https://web.azure.com/{app.host}/{app.name}"
 * Output: "https://web.azure.com/Orange/Example"
 */
export var formatUrlSubpartWithEncoding = function (urlPart, propertyValue, propertyPath, encoder) {
    if (propertyPath === void 0) { propertyPath = []; }
    if (propertyValue !== undefined) {
        if (typeof propertyValue === 'object') {
            for (var field in propertyValue) {
                urlPart = formatUrlSubpartWithEncoding(urlPart, propertyValue[field], propertyPath.concat(field), encoder);
            }
        }
        else {
            var path = propertyPath.join('.');
            var regExPattern = new RegExp("{".concat(path.replace('.', '\\.'), "}"), 'g');
            urlPart = urlPart.replace(regExPattern, encoder(propertyValue));
        }
    }
    return urlPart;
};
/**
 * Adds default values to provided AppContext instance
 */
var getContextWithDefaults = function (context) {
    var _a, _b, _c, _d, _e;
    return {
        actionInfo: {
            actionId: ((_a = context.actionInfo) === null || _a === void 0 ? void 0 : _a.actionId) || '',
            actionObjects: ((_b = context.actionInfo) === null || _b === void 0 ? void 0 : _b.actionObjects) || [],
        },
        user: __assign(__assign({ 
            // @ts-ignore TypeScript is complaining because it knows that id should already exist on context.user.
            // but we want to be extra sure, that it's replaced, even if somehow that's not true
            id: '', licenseType: '' }, context.user), { 
            // make sure we override these after the top-level properties
            tenant: __assign({ id: '', sku: '' }, (_c = context.user) === null || _c === void 0 ? void 0 : _c.tenant) }),
        app: __assign({ iconPositionVertical: 0, parentMessageId: '', userFileOpenPreference: FileOpenPreference.Inline, userClickTime: 0 }, context.app),
        page: __assign({ isFullScreen: false, subPageId: '', isMultiWindow: false, sourceOrigin: '' }, context.page),
        meeting: {
            id: ((_d = context.meeting) === null || _d === void 0 ? void 0 : _d.id) || '',
        },
        chat: {
            id: ((_e = context.chat) === null || _e === void 0 ? void 0 : _e.id) || '',
        },
        channel: context.channel,
        sharePointSite: context.sharePointSite,
        sharepoint: context.sharepoint,
        team: context.team,
    };
};
/**
 * Transforms the AppContext object to the client SDK readable format.
 * It is also the old context object format
 */
export function transformContext(appSDKContext) {
    var _a, _b;
    var context = {
        // actionInfo
        actionInfo: appSDKContext.actionInfo,
        // app
        locale: appSDKContext.app.locale,
        appSessionId: appSDKContext.app.sessionId || '',
        appLaunchId: appSDKContext.app.launchId || '',
        theme: appSDKContext.app.theme,
        appIconPosition: appSDKContext.app.iconPositionVertical || 0,
        osLocaleInfo: appSDKContext.app.osLocaleInfo,
        parentMessageId: appSDKContext.app.parentMessageId || '',
        userClickTime: appSDKContext.app.userClickTime || 0,
        userFileOpenPreference: appSDKContext.app.userFileOpenPreference || FileOpenPreference.Inline,
        // app.host
        hostName: appSDKContext.app.host.name,
        hostClientType: appSDKContext.app.host.clientType,
        sessionId: appSDKContext.app.host.sessionId || '',
        ringId: appSDKContext.app.host.ringId,
        // page
        entityId: appSDKContext.page.id || '',
        frameContext: appSDKContext.page.frameContext,
        subEntityId: appSDKContext.page.subPageId || '',
        isFullScreen: appSDKContext.page.isFullScreen || false,
        isMultiWindow: appSDKContext.page.isMultiWindow || false,
        sourceOrigin: appSDKContext.page.sourceOrigin || '',
        // user
        userObjectId: appSDKContext.user !== undefined ? appSDKContext.user.id : undefined,
        isCallingAllowed: appSDKContext.user !== undefined ? appSDKContext.user.isCallingAllowed : undefined,
        isPSTNCallingAllowed: appSDKContext.user !== undefined ? appSDKContext.user.isPSTNCallingAllowed : undefined,
        userLicenseType: appSDKContext.user !== undefined ? appSDKContext.user.licenseType : '',
        loginHint: appSDKContext.user !== undefined ? appSDKContext.user.loginHint : undefined,
        userPrincipalName: appSDKContext.user !== undefined ? appSDKContext.user.userPrincipalName : undefined,
        upn: appSDKContext.user !== undefined ? appSDKContext.user.userPrincipalName : undefined,
        userDisplayName: appSDKContext.user !== undefined ? appSDKContext.user.displayName : undefined,
        // user.tenant
        tid: appSDKContext.user !== undefined
            ? appSDKContext.user.tenant !== undefined
                ? appSDKContext.user.tenant.id
                : undefined
            : undefined,
        tenantSKU: ((_b = (_a = appSDKContext === null || appSDKContext === void 0 ? void 0 : appSDKContext.user) === null || _a === void 0 ? void 0 : _a.tenant) === null || _b === void 0 ? void 0 : _b.sku) || '',
        // channel
        channelId: appSDKContext.channel !== undefined ? appSDKContext.channel.id : undefined,
        channelName: appSDKContext.channel !== undefined ? appSDKContext.channel.displayName : undefined,
        channelRelativeUrl: appSDKContext.channel !== undefined ? appSDKContext.channel.relativeUrl : undefined,
        channelType: appSDKContext.channel !== undefined ? appSDKContext.channel.membershipType : undefined,
        defaultOneNoteSectionId: appSDKContext.channel !== undefined ? appSDKContext.channel.defaultOneNoteSectionId : undefined,
        hostTeamGroupId: appSDKContext.channel !== undefined ? appSDKContext.channel.ownerGroupId : undefined,
        hostTeamTenantId: appSDKContext.channel !== undefined ? appSDKContext.channel.ownerTenantId : undefined,
        // chat
        chatId: appSDKContext.chat !== undefined ? appSDKContext.chat.id : '',
        // meeting
        meetingId: appSDKContext.meeting !== undefined ? appSDKContext.meeting.id : '',
        // sharepoint
        sharepoint: appSDKContext.sharepoint,
        // team
        teamId: appSDKContext.team !== undefined ? appSDKContext.team.internalId : undefined,
        teamName: appSDKContext.team !== undefined ? appSDKContext.team.displayName : undefined,
        teamType: appSDKContext.team !== undefined ? appSDKContext.team.type : undefined,
        groupId: appSDKContext.team !== undefined ? appSDKContext.team.groupId : undefined,
        teamTemplateId: appSDKContext.team !== undefined ? appSDKContext.team.templateId : undefined,
        isTeamArchived: appSDKContext.team !== undefined ? appSDKContext.team.isArchived : undefined,
        userTeamRole: appSDKContext.team !== undefined ? appSDKContext.team.userRole : undefined,
        // sharepointSite
        teamSiteId: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.teamSiteId : undefined,
        teamSiteUrl: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.teamSiteUrl : undefined,
        teamSiteDomain: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.teamSiteDomain : undefined,
        teamSitePath: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.teamSitePath : undefined,
        mySiteDomain: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.mySiteDomain : undefined,
        mySitePath: appSDKContext.sharePointSite !== undefined ? appSDKContext.sharePointSite.mySitePath : undefined,
    };
    return context;
}
//# sourceMappingURL=domainValidation.js.map